import { useCallback, useEffect, useReducer } from 'react';

import useLabels from '@/hooks/useLabelsParcel';
import useServerPagination from '@/hooks/useServerPagination';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import {
  IResOrderError,
  IResOrderOK,
  IResOrderUnauthorized,
} from '@/types/parcel/orders/orderById.interface';
import {
  IReqPickups,
  IResPickupsError,
  IResPickupsOK,
  IResPickupsUnauthorized,
} from '@/types/parcel/orders/pickups.interface';
import { updateReducerState } from '@/utils/common';

import type { PickupActions, StatePickup } from './interfaces';

const deliveriesTypesNotAvailable = ['NAL', '99M', 'CO2F'];

const initialState: StatePickup = {
  downloadModal: false,
  detailModal: false,
  scheduleModal: false,
  search: '',
  trackingIds: [],
  singleTrackingId: null,
  shipments: {
    loading: false,
    error: null,
    data: [],
    totalPages: 0,
  },
  shipmentDetails: {
    loading: false,
    error: null,
    data: null,
  },
};

const reducer = (state: StatePickup, action: PickupActions): StatePickup => {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const usePickups = () => {
  const pagination = useServerPagination();
  const [state, dispatch] = useReducer(reducer, initialState);

  const updateState = (
    updates: Partial<StatePickup> | ((prevState: StatePickup) => Partial<StatePickup>),
  ) => {
    updateReducerState(dispatch, state, updates);
  };

  const fetchShipments = useCallback(
    async ({ search }: IReqPickups) => {
      updateState((prevState) => ({ shipments: { ...prevState.shipments, loading: true } }));
      const http = initializeHttpProvider();
      try {
        const response = await http.orders.pickups({
          page: pagination.filters.page,
          pageSize: pagination.filters.page_size,
          search,
        });
        const data = (response as IResPickupsOK).data;

        const filteredOrders = data.ordersData.filter(
          (order) =>
            order.statusNameES !== 'Cancelado' &&
            order.statusNameES !== 'Cancelada' &&
            order.statusNameEN !== 'Cancelled',
        );
        updateState((prevState) => ({
          shipments: {
            ...prevState.shipments,
            loading: false,
            data: filteredOrders,
            totalPages: data.totalPages,
          },
        }));
      } catch (error) {
        updateState((prevState) => ({
          shipments: {
            ...prevState.shipments,
            loading: false,
            error: error as IResPickupsError | IResPickupsUnauthorized,
          },
        }));
      }
    },
    [pagination.filters],
  );

  const fetchShipmentsByTrackingId = useCallback(async (trackingId: string) => {
    updateState((prevState) => ({
      shipmentDetails: { ...prevState.shipmentDetails, loading: true },
    }));
    const http = initializeHttpProvider();
    try {
      const response = await http.orders.orderById({ trackingId });
      const data = (response as IResOrderOK).data;
      updateState((prevState) => ({
        shipmentDetails: {
          ...prevState.shipmentDetails,
          loading: false,
          data: data,
        },
        detailModal: true,
      }));
    } catch (error) {
      updateState((prevState) => ({
        shipmentDetails: {
          ...prevState.shipmentDetails,
          loading: false,
          error: error as IResOrderError | IResOrderUnauthorized,
        },
      }));
    }
  }, []);

  useEffect(() => {
    void fetchShipments({});
  }, []);

  useEffect(() => {
    fetchShipments({ search: state.search });
  }, [pagination.filters]);

  // Search Input ----------------------

  const onChgSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (value.length >= 5) {
        updateState({ search: value });
        void fetchShipments({ search: value });
        pagination.onChangeFilter({ page: 0 });
      }
      if (value.length === 0) {
        updateState({ search: '' });
        void fetchShipments({});
        pagination.onChangeFilter({ page: 0 });
      }
    },
    [fetchShipments, pagination],
  );

  const onSearch = useCallback(() => {
    void fetchShipments({ search: state.search });
    pagination.onChangeFilter({ page: 0 });
  }, [fetchShipments, pagination, state.search]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  // Select rows

  const selectRows = useCallback((trackings: string[]) => {
    if (trackings.length > 10) {
      return;
    }
    updateState({ trackingIds: trackings });
  }, []);

  const cleanTrackingIds = useCallback(() => {
    updateState({ trackingIds: [] });
  }, []);

  // Download modal

  const handleOpenDownloadModal = (trackingId?: string) => {
    updateState({ downloadModal: true });
    if (trackingId) {
      updateState({ singleTrackingId: trackingId });
    } else {
      updateState({ singleTrackingId: null });
    }
  };

  const handleCloseDownloadModal = () => {
    updateState({ downloadModal: false, singleTrackingId: null });
  };

  // Details modal

  const handleCloseDetailModal = () => {
    updateState((prevState) => ({
      shipmentDetails: { ...prevState.shipmentDetails, data: null },
      detailModal: false,
    }));
  };

  // schedule

  const handleOpenScheduleModal = () => {
    updateState({ scheduleModal: true });
  };

  const handleCloseScheduleModal = () => {
    updateState({ scheduleModal: false });
  };

  // useLabels ------

  const { downloadLabels, loading } = useLabels({
    cleanTrackingIds,
    onCloseModal: handleCloseDownloadModal,
  });

  return {
    ...state,
    handleOpenScheduleModal,
    handleCloseScheduleModal,
    pagination,
    onChgSearch,
    onSearch,
    handleKeyPress,
    downloadLabels,
    loading,
    deliveriesTypesNotAvailable,
    selectRows,
    handleOpenDownloadModal,
    handleCloseDownloadModal,
    handleCloseDetailModal,
    fetchShipments,
    fetchShipmentsByTrackingId,
  };
};

export default usePickups;
